import { Platform } from 'react-native';
import {
    addDays,
    subDays,
    getDay,
    getHours as getDateHours,
    getMinutes as getDateMinutes,
    differenceInHours,
    differenceInMinutes,
    isSameDay,
    isWithinInterval,
    isBefore,
    format
} from 'date-fns';

export const SWIPE_THRESHOLD  = 50;
export const MIN_HEIGHT       = 1200;
export const HOUR_GUIDE_WIDTH = 50;
export const OVERLAP_OFFSET   = Platform.OS === 'web' ? 20 : 8;
export const OVERLAP_PADDING  = Platform.OS === 'web' ? 3 : 0;
export const DAY_MINUTES      = 1440;
export const SCALE_FACTOR = 1/16;
export const PERCENT_FACTOR = 100;

export const Color = Object.freeze({
    primary: 'rgb(66, 133, 244)',
    red: '#C80B22',
    yellow: '#F8E71C',
    green: '#4AC001',
    orange: '#E26245',
    pink: '#5934C7'
});

export const isToday = date =>
{
    return isSameDay(new Date(), date);
};

export const getHours = (start, end) => 
{
    const is24 = !start && !end;    
    let root   = start+1;

    if (is24) {
        return Array(24).fill(0).map((el, index) => index);
    }
    else {
        return Array(end - start).fill(0).map((el, index) => {
            if (index === 0) {
                return root;
            }

            return (root + index);
        });
    }
};

export const getDatesInWeek = (date, weekStartsOn) =>
{
    if (!date) {
        date = new Date();
    }

    if (!weekStartsOn) {
        weekStartsOn = 0;
    }

    const cloned = new Date(date);
    const currentDayOfWeek = getDay(cloned);

    return Array(7).fill(0).map((value, index) => {
        const diff = (index - currentDayOfWeek + weekStartsOn);

        let result;

        if (diff < 0) {
            result = subDays(cloned, Math.abs(diff));
        }
        else {
            result = addDays(cloned, diff);
        }

        return result;
    });
};

export const getDatesInNextThreeDays = date =>
{
    if (!date) {
        date = new Date();
    }

    return Array(3).fill(0).map((value, index) => {
        return addDays(date, index);
    });
};

export const getDatesInNextOneDay = date =>
{
    if (!date) {
        date = new Date();
    }

    return Array(1).fill(0).map(function (value, index) {
        return addDays(date, index);
    });
};

export const getRelativeTopInDay = date =>
{
    if (!date) {
        date = new Date();
    }

    const hour = getDateHours(date) - 7
    const minuteToHour = getDateMinutes(date)/60
    return (hour + minuteToHour)*SCALE_FACTOR*PERCENT_FACTOR
};

export const isAllDayEvent = event =>
{
    return (event.isAllDayEvent || (getDateHours(event.start) === 0 && getDateMinutes(event.start) === 0 && getDateHours(event.end) === 0 && getDateMinutes(event.end) === 0));
};

const isBetween = (date, start, end) => {
    return isWithinInterval(date, { start, end });
};

export const getCountOfEventsAtEvent = (event, eventList) =>
{
    return eventList.filter(e => {
        return isBetween(event.start, e.start, e.end) || isBetween(e.start, event.start, event.end);
    }).length;
};

export const getOrderOfEvent = (event, eventList) =>
{
    const events = eventList.filter(e => {
        return isBetween(event.start, e.start, e.end) || isBetween(e.start, event.start, event.end);
    }).sort((a, b) => {
        if (isSameDay(a.start, b.start) && getDateHours(a.start) === getDateHours(b.start) && getDateMinutes(a.start) === getDateMinutes(b.start)) {
            return differenceInMinutes(a.start, a.end) < differenceInMinutes(b.start, b.end) ? -1 : 1;
        }
        else {
            return isBefore(a.start, b.start) ? -1 : 1;
        }
    });

    return events.indexOf(event);
};

export const getColorForEventPosition = eventPosition =>
{
    switch (eventPosition % 5) {
        case 0:
            return Color.primary;
        case 1:
            return Color.orange;
        case 2:
            return Color.green;
        case 3:
            return Color.red;
        case 4:
            return Color.pink;
        default:
            return Color.primary;
    }
};

export const getStyleForOverlappingEvent = (eventCount, eventPosition) =>
{
    if (eventCount > 1) {
        const normalizedPosition = eventPosition + 1;
        const start              = eventPosition * OVERLAP_OFFSET;
        const end                = eventCount === normalizedPosition ? 0 : (eventCount - normalizedPosition) * OVERLAP_OFFSET;
        const  zIndex            = 100 + eventPosition;

        return {
            start: start + OVERLAP_PADDING,
            end: end + OVERLAP_PADDING,
            backgroundColor: getColorForEventPosition(eventPosition),
            zIndex: zIndex,
        };
    }

    return {};
};

export const getEventCellPositionStyle = (event) =>
{
    const end            = event.end;
    const start          = event.start;
    const relativeHeight = SCALE_FACTOR * differenceInHours(end, start) * PERCENT_FACTOR;

    const relativeTop    = getRelativeTopInDay(start);

    return {
        height: relativeHeight + '%',
        top: relativeTop + '%',
    };
};

export const modeToNumber = mode => 
{
    if (!mode) {
        mode = 'week';
    }
    
    switch (mode) {
        case '3days':
            return 3;
        case 'week':
            return 7;
        default:
            throw new Error('undefined mode');
    }
};

export const formatStartEnd = event =>
{
    return format(event.start, 'hh:mm') + ' - ' + format(event.end, 'hh:mm');
};

export const formatHour = hour =>
{
    const ampm = hour >= 12 ? 'PM' : 'AM';

    hour = hour % 12;
    hour = hour ? hour : 12;
    
    return hour + ':00 ' + ampm;
}
